import React, { useEffect, useState } from "react";
import "../../InvoiceModal.css";
import { fixedDecimals } from "../../../../helpers/helpers";
import { useTranslation } from "react-i18next";

const QuotePrice = ({ data }) => {
  const { t } = useTranslation("finances");
  const hasInsurance = !!data?.insurance;
  const hasShipmentCharges = !!data?.quoteCharges;

  const [taxCharges, setTaxCharges] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([]);

  useEffect(() => {
    const getUniqueCharges = (type) =>
      data?.quoteCharges
        ?.flatMap((item) => item?.[type] || [])
        ?.filter(
          (charge, index, self) =>
            charge &&
            index === self.findIndex((c) => c?.NameEn === charge?.NameEn)
        );

    setTaxCharges(getUniqueCharges("tax_charges"));
    setAdditionalCharges(getUniqueCharges("additional_charges"));
  }, [data]);

  const findChargeByNameEn = (nameEn, index, type) => {
    const charges = data?.quoteCharges[index]?.[type];
    return charges?.find((charge) => charge?.NameEn === nameEn) || false;
  };

  const findTaxChargeByNameEn = (nameEn, index) =>
    findChargeByNameEn(nameEn, index, "tax_charges");

  const findAdditionalChargeByNameEn = (nameEn, index) =>
    findChargeByNameEn(nameEn, index, "additional_charges");

  return (
    <div className="invoice__billing_admin_wrap">
      <div className="invmodal__shipment_detailshding">
        {t("invoice.quote.title")}{" "}
      </div>
      <small>
        {t("invoice.quote.hint")}
      </small>

      <div className="overflow-x-auto">
        <table className="w-full min-w-max mb-2 text-[10px] md:text-xxs lg:text-xs xl:text-sm font-medium">
          <thead>
            <tr>
              <th className="p-1 text-center font-semibold">#</th>
              {data?.quoteCharges?.[0]?.base_price > 0 && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.base")}
                </th>
              )}
              {data?.quoteCharges?.[0]?.fuel_charge > 0 && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.fuel")}
                </th>
              )}
              {data?.quoteCharges?.[0]?.freight_charge > 0 && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.freight")}
                </th>
              )}
              {data?.quoteCharges?.[0]?.signature_charge > 0 && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.signature")}
                </th>
              )}
              {data?.quoteCharges?.[0]?.residential_charge > 0 && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.residential")}
                </th>
              )}
              {data?.quoteCharges?.[0]?.PickupCharge > 0 && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.pickup")}
                </th>
              )}
              {data?.quoteCharges?.[0]?.tailgate_charge > 0 && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.tailgate")}
                </th>
              )}
              {hasInsurance && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.insurance")}
                </th>
              )}
              {hasShipmentCharges &&
                additionalCharges?.map((charge, index) => (
                  <th className="p-1 text-center font-semibold" key={index}>
                    {charge?.NameEn}
                  </th>
                ))}
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.subTotal")}
              </th>
              {hasShipmentCharges &&
                taxCharges?.map((tax, index) => (
                  <th className="p-1 text-center font-semibold" key={index}>
                    {tax?.NameEn}
                  </th>
                ))}
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.quoteCharges?.map((charge, index) => (
              <tr key={index}>
                <td className="p-1 text-center">{index + 1}</td>
                {[
                  ...(charge?.base_price > 0 ? ["base_price"] : []),
                  ...(charge?.fuel_charge > 0 ? ["fuel_charge"] : []),
                  ...(charge?.freight_charge > 0 ? ["freight_charge"] : []),
                  ...(charge?.signature_charge > 0 ? ["signature_charge"] : []),
                  ...(charge?.residential_charge > 0
                    ? ["residential_charge"]
                    : []),
                  ...(charge?.PickupCharge > 0 ? ["PickupCharge"] : []),
                  ...(charge?.tailgate_charge > 0 ? ["tailgate_charge"] : []),
                  ...(hasInsurance ? ["insurance_charge"] : []),
                ].map((key) => (
                  <td key={key} className="p-1 text-center">
                    {charge?.[key] && charge?.[key] !== 0 ? charge?.[key] : "-"}
                  </td>
                ))}
                {hasShipmentCharges &&
                  additionalCharges?.map((charge, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      {findAdditionalChargeByNameEn(charge?.NameEn, index)
                        ?.Charge ?? "-"}
                    </td>
                  ))}
                <td className="p-1 text-center">
                  {charge?.sub_total && charge?.sub_total !== 0
                    ? charge?.sub_total
                    : "-"}
                </td>
                {hasShipmentCharges &&
                  taxCharges?.map((tax, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      {findTaxChargeByNameEn(tax?.NameEn, index)?.Charge ?? "-"}
                    </td>
                  ))}
                <td className="p-1 text-center">
                  {charge?.total && charge?.total !== 0
                    ? fixedDecimals(charge?.total)
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuotePrice;
